import React, { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { RESOURCES } from "../config/resources";
import { fmtEUR, fmtPct, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search, Eye, KeyRound, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import PortalAccessDialog from "./investors/PortalAccessDialog";

const downloadBlob = async (url, name) => {
  const r = await api.get(url, { responseType: "blob" });
  const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u);
};

const WRITE_ROLES = {
  companies: ["super_admin", "amministrazione"],
  operations: ["super_admin", "amministrazione", "project_manager"],
  investors: ["super_admin", "amministrazione"],
  investments: ["super_admin", "amministrazione"],
  accounts: ["super_admin", "amministrazione"],
  movements: ["super_admin", "amministrazione"],
  suppliers: ["super_admin", "amministrazione", "project_manager", "responsabile_cantiere"],
  orders: ["super_admin", "amministrazione", "project_manager"],
};

export default function ResourcePage({ resource }) {
  const cfg = RESOURCES[resource];
  const { user } = useAuth();
  const nav = useNavigate();
  const hasDetail = resource === "operations" || resource === "investors";
  const detailPath = resource === "operations" ? "/operazioni" : "/investitori";
  const [accessFor, setAccessFor] = useState(null);
  const isAdmin = ["super_admin", "amministrazione"].includes(user?.role);
  const canWrite = user?.role === "super_admin" || (WRITE_ROLES[resource] || []).includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refs, setRefs] = useState({});
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [delItem, setDelItem] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const id = params.get("edit");
    if (id && rows.length) { const row = rows.find((r) => r.id === id); if (row) { openEdit(row); setParams({}, { replace: true }); } }
  }, [rows, params]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/${cfg.endpoint}`); setRows(data); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const refFields = cfg.fields.filter((f) => f.type === "ref");
    const uniq = [...new Set(refFields.map((f) => f.ref))];
    uniq.forEach((r) => api.get(`/${r}`).then(({ data }) => setRefs((p) => ({ ...p, [r]: data }))).catch(() => {}));
  }, [resource]);

  const refLabel = (refName, id) => {
    const item = (refs[refName] || []).find((x) => x.id === id);
    const f = cfg.fields.find((ff) => ff.ref === refName);
    if (!item) return "—";
    if (refName === "investors") return item.ragione_sociale || `${item.nome || ""} ${item.cognome || ""}`.trim() || item.codice;
    return item[f.refLabel] || item.codice;
  };

  const openNew = () => { setEditing(null); const init = {}; cfg.fields.forEach((f) => { if (f.type === "bool") init[f.k] = f.k === "actual" ? true : (f.k === "visibile_portale" || f.k === "attivo"); }); setForm(init); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const save = async () => {
    for (const f of cfg.fields) if (f.req && !form[f.k]) { toast.error(`Campo obbligatorio: ${f.l}`); return; }
    setSaving(true);
    const payload = { ...form };
    cfg.fields.forEach((f) => { if ((f.type === "number" || f.type === "currency") && payload[f.k] !== undefined && payload[f.k] !== "") payload[f.k] = parseFloat(payload[f.k]); });
    try {
      if (editing) await api.put(`/${cfg.endpoint}/${editing.id}`, payload);
      else await api.post(`/${cfg.endpoint}`, payload);
      toast.success(editing ? "Aggiornato con successo" : "Creato con successo");
      setOpen(false); load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    setSaving(false);
  };

  const doDelete = async () => {
    try { await api.delete(`/${cfg.endpoint}/${delItem.id}`); toast.success("Eliminato (spostato nel cestino)"); setDelItem(null); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  const renderCell = (row, col) => {
    const [key, kind] = col.split(":");
    let v = row[key];
    if (kind === "cur") return <span className={v < 0 ? "text-destructive font-medium" : "font-medium"}>{fmtEUR(v)}</span>;
    if (kind === "pct") return fmtPct(v);
    if (kind === "date") return fmtDate(v);
    const refField = cfg.fields.find((f) => f.k === key && f.type === "ref");
    if (refField) return refLabel(refField.ref, v);
    if (key === "stato" || key === "direction") return <Badge variant="outline" className="capitalize">{v || "—"}</Badge>;
    return v ?? "—";
  };

  const filtered = useMemo(() => {
    let r = rows.filter((row) => !search || JSON.stringify(row).toLowerCase().includes(search.toLowerCase()));
    if (sortKey) r = [...r].sort((a, b) => { const av = a[sortKey], bv = b[sortKey]; if (av == null) return 1; if (bv == null) return -1; return (av > bv ? 1 : -1) * sortDir; });
    return r;
  }, [rows, search, sortKey, sortDir]);

  const COL_LABELS = { prezzo_acquisto: "Prezzo acquisto", costi_accessori_previsti: "Costi (lavori, mobili, progett., agenzia…)", costo_totale_previsto: "Costi totali", ricavi_previsti: "Ricavi previsti (vendite unità)", margine_previsto: "Margine lordo", margine_netto_previsto: "Margine netto (dopo quota utili)", roi_previsto: "ROI", roe_previsto: "ROE (su capitale proprio)", apporti_associati_versati: "Capitale da associati", investor_id: "Associato", supplier_id: "Fornitore", operation_id: "Operazione", capitale_promesso: "Apporto promesso", capitale_versato: "Apporto versato", rendimento_previsto: "Quota utili prevista", importo_totale_dovuto: "Totale dovuto all'associato", capitale_disponibile: "Apporto disponibile dichiarato", nome_operazione: "Operazione", nome_conto: "Conto", costo_totale_previsto: "Costo previsto", margine_previsto: "Margine previsto", roi_previsto: "ROI previsto", saldo_contabile: "Saldo contabile", saldo_disponibile: "Saldo disponibile", disponibilita_totale: "Disponibilità totale", data_pagamento: "Data", partita_iva: "Partita IVA", forma_giuridica: "Forma giuridica", category: "Categoria", direction: "Entrata/Uscita" };
  const headerCols = cfg.columns.map((c) => c.split(":")[0]);

  return (
    <div className="space-y-5" data-testid={`page-${resource}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold text-primary">{cfg.title}</h1>
          <p className="text-sm text-muted-foreground">{filtered.length} record</p>
        </div>
        {canWrite && <Button onClick={openNew} data-testid={`new-${resource}-btn`}><Plus className="w-4 h-4 mr-1" /> Nuovo</Button>}
      </div>

      <Card className="p-4">
        <div className="relative max-w-sm mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtra..." className="pl-9" data-testid={`filter-${resource}`} />
        </div>
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                {cfg.columns.map((c) => { const k = c.split(":")[0]; return (
                  <TableHead key={c} className="cursor-pointer whitespace-nowrap" onClick={() => { setSortKey(k); setSortDir(sortKey === k ? -sortDir : 1); }}>
                    {COL_LABELS[k] || k.replace(/_/g, " ")}
                  </TableHead>); })}
                {(canWrite || hasDetail || resource === "investors" || resource === "investments") && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={99} className="text-center py-8 text-muted-foreground">Caricamento...</TableCell></TableRow>}
              {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={99} className="text-center py-8 text-muted-foreground">Nessun dato</TableCell></TableRow>}
              {filtered.map((row) => (
                <TableRow key={row.id} className="hover:bg-muted/40" data-testid={`row-${resource}`}>
                  {cfg.columns.map((c) => <TableCell key={c}>{renderCell(row, c)}</TableCell>)}
                  {(canWrite || hasDetail || resource === "investors" || resource === "investments") && (
                    <TableCell className="text-right whitespace-nowrap">
                      {hasDetail && <Button variant="ghost" size="icon" onClick={() => nav(`${detailPath}/${row.id}`)} title="Dettaglio" data-testid={`detail-${resource}`}><Eye className="w-4 h-4" /></Button>}
                      {resource === "investors" && isAdmin && <Button variant="ghost" size="icon" onClick={() => setAccessFor(row)} title="Accesso portale" data-testid="investor-portal-access-btn"><KeyRound className="w-4 h-4" /></Button>}
                      {resource === "investments" && <Button variant="ghost" size="icon" onClick={() => downloadBlob(`/investments/${row.id}/contract-pdf`, `contratto_AIP_${row.codice}.pdf`).catch(() => toast.error("Generazione PDF non riuscita"))} title="Contratto AIP PDF" data-testid="investment-contract-pdf-btn"><FileSignature className="w-4 h-4" /></Button>}
                      {canWrite && <Button variant="ghost" size="icon" onClick={() => openEdit(row)} data-testid={`edit-${resource}`}><Pencil className="w-4 h-4" /></Button>}
                      {canWrite && <Button variant="ghost" size="icon" onClick={() => setDelItem(row)} data-testid={`delete-${resource}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Modifica ${cfg.singular}` : `Nuovo ${cfg.singular}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {cfg.fields.map((f) => (
              <div key={f.k} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                <Label className="text-xs">{f.l}{f.req && <span className="text-destructive"> *</span>}</Label>
                {f.type === "select" ? (
                  <Select value={form[f.k] || ""} onValueChange={(v) => setForm({ ...form, [f.k]: v })}>
                    <SelectTrigger data-testid={`field-${f.k}`}><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>{f.opts.map((o) => <SelectItem key={o} value={o} className="capitalize">{o.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.type === "ref" ? (
                  <Select value={form[f.k] || ""} onValueChange={(v) => setForm({ ...form, [f.k]: v })}>
                    <SelectTrigger data-testid={`field-${f.k}`}><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>{(refs[f.ref] || []).map((o) => <SelectItem key={o.id} value={o.id}>{f.ref === "investors" ? (o.ragione_sociale || `${o.nome || ""} ${o.cognome || ""}`.trim() || o.codice) : (o[f.refLabel] || o.codice)}</SelectItem>)}</SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <Textarea value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`field-${f.k}`} />
                ) : f.type === "bool" ? (
                  <div className="flex items-center h-10"><Switch checked={!!form[f.k]} onCheckedChange={(v) => setForm({ ...form, [f.k]: v })} data-testid={`field-${f.k}`} /></div>
                ) : (
                  <Input type={f.type === "number" || f.type === "currency" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`field-${f.k}`} />
                )}
              </div>
            ))}
          </div>
          {cfg.computed && editing && (
            <Card className="p-3 bg-muted/40 mt-2">
              <div className="text-xs font-semibold mb-2 uppercase text-muted-foreground">Valori calcolati automaticamente</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                {cfg.computed.map((c) => (
                  <div key={c}><div className="text-xs text-muted-foreground">{c.replace(/_/g, " ")}</div>
                    <div className="font-semibold">{c.startsWith("roi") ? fmtPct(editing[c]) : fmtEUR(editing[c])}</div></div>
                ))}
              </div>
            </Card>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="save-btn">{saving ? "Salvataggio..." : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi l'eliminazione?</AlertDialogTitle>
            <AlertDialogDescription>Il record verrà spostato nel cestino (soft delete) e potrà essere ripristinato.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-btn">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PortalAccessDialog investor={accessFor} onClose={() => setAccessFor(null)} />
    </div>
  );
}
